(function (global) {
  'use strict';

  function findVariantById(product, variantId) {
    if (!product || !Array.isArray(product.variants) || variantId == null) return null;

    var normalizedId = String(variantId);
    return (
      product.variants.find(function (variant) {
        if (!variant || variant.id == null) return false;
        var variantIdParts = String(variant.id).split('/');
        return variantIdParts[variantIdParts.length - 1] === normalizedId;
      }) || null
    );
  }

  function isBackorderVariant(variant) {
    if (!variant) return false;

    if (
      typeof variant.inventory_quantity === 'number' &&
      typeof variant.inventory_policy === 'string'
    ) {
      return variant.inventory_quantity <= 0 && variant.inventory_policy === 'continue';
    }

    return (
      typeof variant.quantityAvailable === 'number' &&
      variant.quantityAvailable <= 0 &&
      variant.availableForSale === true
    );
  }

  function normalizeVariantId(variantId) {
    var parts = String(variantId == null ? '' : variantId).split('/');
    return parts[parts.length - 1];
  }

  function getBackorderShipWindow(variant) {
    if (!variant || !variant.backorderShipWindow) return '';

    var value =
      typeof variant.backorderShipWindow === 'string'
        ? variant.backorderShipWindow
        : variant.backorderShipWindow.value;
    return typeof value === 'string' ? value.trim() : '';
  }

  function formatExpectedShipText(shipWindow) {
    var normalizedWindow = String(shipWindow || '').trim().replace(/\.+$/, '');
    return normalizedWindow
      ? 'Expected to ship ' + normalizedWindow + '.'
      : 'Shipping date to be confirmed.';
  }

  function buildBundleShippingSummary(backorders) {
    var items = Array.isArray(backorders) ? backorders : [];
    if (!items.length) return '';

    var countLabel = items.length === 1 ? '1 item is' : items.length + ' items are';
    var windows = items
      .map(function (item) {
        return String((item && item.shipWindow) || '').trim().replace(/\.+$/, '');
      })
      .filter(Boolean);
    var uniqueWindows = Array.from(new Set(windows));

    if (uniqueWindows.length === 1 && windows.length === items.length) {
      return (
        countLabel +
        ' on backorder. Your complete bundle is expected to ship together ' +
        uniqueWindows[0] +
        '.'
      );
    }

    var suffix = windows.length
      ? ' Review the item estimates above.'
      : ' Shipping date to be confirmed.';
    return (
      countLabel +
      ' on backorder. Your complete bundle will ship together when all items are available.' +
      suffix
    );
  }

  function decorateBundlyCartFormData(formData, backordersByVariant) {
    if (!formData || typeof formData.entries !== 'function') return formData;

    var entries = Array.from(formData.entries());
    var isBundlyRequest = entries.some(function (entry) {
      return /^items\[\d+\]\[properties\]\[_bundly__bundle_id\]$/.test(entry[0]);
    });
    if (!isBundlyRequest) return formData;

    entries.forEach(function (entry) {
      var match = entry[0].match(/^items\[(\d+)\]\[id\]$/);
      if (!match) return;

      var index = match[1];
      var status = backordersByVariant.get(normalizeVariantId(entry[1]));
      if (!status) return;

      var properties = {
        Availability: 'Backorder',
        'Expected ship window': status.shipWindow || 'To be confirmed',
        'Bundle shipping': 'Ships with complete bundle',
      };

      Object.keys(properties).forEach(function (name) {
        var key = 'items[' + index + '][properties][' + name + ']';
        if (!formData.has(key)) formData.append(key, properties[name]);
      });
    });

    return formData;
  }

  function getBackorderStatus(product, variantId) {
    var variant = findVariantById(product, variantId);
    return {
      isBackorder: isBackorderVariant(variant),
      variant: variant,
    };
  }

  var api = {
    buildBundleShippingSummary: buildBundleShippingSummary,
    decorateBundlyCartFormData: decorateBundlyCartFormData,
    findVariantById: findVariantById,
    formatExpectedShipText: formatExpectedShipText,
    getBackorderShipWindow: getBackorderShipWindow,
    isBackorderVariant: isBackorderVariant,
    getBackorderStatus: getBackorderStatus,
  };

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  }

  if (!global || !global.document) return;

  var document = global.document;
  var productRequests = new Map();
  var cardStatuses = new WeakMap();
  var selectedBackorders = new Map();
  var componentSelector =
    'bundly-product-component-card[data-product-handle][data-selected-variant-id]';

  function getStorefrontConfig() {
    var scripts = document.querySelectorAll(
      '[data-block-handle="bundle_information"] script[type="application/json"]'
    );

    for (var index = 0; index < scripts.length; index += 1) {
      try {
        var data = JSON.parse(scripts[index].textContent);
        if (data && data.storefrontApiConfig) return data.storefrontApiConfig;
      } catch (error) {}
    }

    return null;
  }

  function loadProduct(handle) {
    if (!productRequests.has(handle)) {
      var config = getStorefrontConfig();
      if (!config || !config.apiVersion || !config.storefrontToken) {
        return Promise.reject(new Error('Bundly Storefront API configuration not found'));
      }

      var query =
        'query BackorderProduct($handle: String!) {' +
        ' product(handle: $handle) { title' +
        ' variants(first: 100) {' +
        ' nodes { id title availableForSale quantityAvailable' +
        ' backorderShipWindow: metafield(namespace: "custom", key: "backorder_ship_window") { value }' +
        ' }' +
        ' }' +
        ' }' +
        '}';

      productRequests.set(
        handle,
        global
          .fetch('/api/' + config.apiVersion + '/graphql.json', {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
              'X-Shopify-Storefront-Access-Token': config.storefrontToken,
            },
            body: JSON.stringify({ query: query, variables: { handle: handle } }),
          })
          .then(function (response) {
            if (!response.ok) {
              throw new Error('Product lookup failed with status ' + response.status);
            }
            return response.json();
          })
          .then(function (result) {
            if (result.errors && result.errors.length) {
              throw new Error(result.errors[0].message || 'Storefront API query failed');
            }

            var product = result.data && result.data.product;
            if (!product || !product.variants) {
              throw new Error('Storefront API product was not found');
            }

            return {
              title: product.title,
              variants: product.variants.nodes || [],
            };
          })
          .catch(function (error) {
            productRequests.delete(handle);
            throw error;
          })
      );
    }

    return productRequests.get(handle);
  }

  function removeBadge(card) {
    var badge = card.querySelector('.bundly-backorder-badge');
    if (badge) badge.remove();
    var message = card.querySelector('.bundly-backorder-message');
    if (message) message.remove();
  }

  function showBadge(card, status) {
    var badge = card.querySelector('.bundly-backorder-badge');
    if (!badge) {
      badge = document.createElement('span');
      badge.className = 'bundly-backorder-badge';
      badge.setAttribute('role', 'status');
      badge.setAttribute('aria-live', 'polite');
      badge.title = 'Available to order while temporarily out of stock';
      badge.textContent = 'Backorder';

      var host =
        card.querySelector('.bundly__component_heading') ||
        card.querySelector('.bundly__product_availability') ||
        card.querySelector('.bundly__component_info') ||
        card;
      host.appendChild(badge);
    }

    var message = card.querySelector('.bundly-backorder-message');
    if (!message) {
      message = document.createElement('div');
      message.className = 'bundly-backorder-message';
      var messageHost =
        card.querySelector('.bundly__product_availability') ||
        card.querySelector('.bundly__component_info') ||
        card;
      messageHost.appendChild(message);
    }
    message.textContent = formatExpectedShipText(status.shipWindow);
  }

  function refreshBundleSummary(cards) {
    selectedBackorders.clear();
    var backorders = [];

    cards.forEach(function (card) {
      var status = cardStatuses.get(card);
      if (!status || !status.isBackorder) return;
      selectedBackorders.set(normalizeVariantId(status.variant.id), status);
      backorders.push(status);
    });

    var buyButtons = document.querySelector('bundly-buy-buttons');
    if (!buyButtons || !buyButtons.parentElement) return;

    var summary = document.querySelector('.bundly-backorder-summary');
    var text = buildBundleShippingSummary(backorders);
    if (!text) {
      if (summary) summary.remove();
      return;
    }

    if (!summary) {
      summary = document.createElement('div');
      summary.className = 'bundly-backorder-summary';
      summary.setAttribute('role', 'note');
      summary.setAttribute('aria-live', 'polite');
      buyButtons.parentElement.insertBefore(summary, buyButtons);
    }
    summary.textContent = text;
  }

  function refreshCard(card) {
    var handle = card.dataset.productHandle;
    var selectedVariantId = card.dataset.selectedVariantId;

    if (!handle || !selectedVariantId) {
      cardStatuses.delete(card);
      removeBadge(card);
      return Promise.resolve();
    }

    return loadProduct(handle)
      .then(function (product) {
        if (card.dataset.selectedVariantId !== selectedVariantId) return;

        var status = getBackorderStatus(product, selectedVariantId);
        status.shipWindow = getBackorderShipWindow(status.variant);
        status.productTitle = product.title;
        cardStatuses.set(card, status);
        if (status.isBackorder) showBadge(card, status);
        else removeBadge(card);
      })
      .catch(function () {
        cardStatuses.delete(card);
        removeBadge(card);
      });
  }

  function refreshAllCards() {
    var cards = Array.prototype.slice.call(document.querySelectorAll(componentSelector));
    return Promise.allSettled(cards.map(refreshCard)).then(function () {
      refreshBundleSummary(cards);
    });
  }

  var originalFetch = global.fetch.bind(global);
  global.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : input && input.url;
    if (
      /\/cart\/add\.js(?:[?#]|$)/.test(String(url || '')) &&
      init &&
      typeof global.FormData !== 'undefined' &&
      init.body instanceof global.FormData
    ) {
      decorateBundlyCartFormData(init.body, selectedBackorders);
    }
    return originalFetch(input, init);
  };

  document.addEventListener('DOMContentLoaded', refreshAllCards);
  document.addEventListener('bundly:afterLoadBundle', refreshAllCards);
  document.addEventListener('bundly:afterChangeVariantOption', refreshAllCards);
  document.addEventListener('shopify:section:load', refreshAllCards);

  global.MessageBundlyBackorders = Object.assign({}, api, {
    refresh: refreshAllCards,
  });
})(typeof window !== 'undefined' ? window : null);
